import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./ProgramCatalog.scss";
import NavBar from "../../shared/components/NavBar/NavBar";
import Footer from "../../shared/components/Footer/Footer";
import PageHeader from "../../shared/components/PageHeader/PageHeader";
import ProgramCard from "../../shared/components/ProgramCard/ProgramCard";
import { cardData } from "../../shared/programCatalogData";

type Course = ReturnType<typeof cardData>[number];

const isOpen = (c: Course) => !!c.currentState?.toLowerCase().includes("open");

const slugify = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const specIcons: Record<string, string> = {
  "Build Your Own CPU": "bi-cpu",
  "Internet-of-Things": "bi-wifi",
  "Art of IC Design": "bi-motherboard",
  "Artificial Intelligence": "bi-robot",
  Cybersecurity: "bi-shield-lock",
  "Quantum Computing": "bi-stars",
};

const filters: { key: string; label: string; match: (c: Course) => boolean }[] = [
  { key: "all", label: "All Programs", match: () => true },
  { key: "short", label: "Short Courses", match: (c) => c.type === "Short Course" },
  { key: "workshop", label: "Workshops", match: (c) => c.type === "Workshop" },
  { key: "on-demand", label: "On-Demand", match: (c) => !!c.onDemand },
];

const ProgramCatalog: React.FC = () => {
  // Programs open for registration or enrollment are listed first.
  const [courses] = useState(() =>
    [...cardData()].sort(
      (a, b) => Number(isOpen(b)) - Number(isOpen(a))
    )
  );
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");

  // Specializations come from the catalog data, most programs first. The
  // selection lives in the URL (?specialization=build-your-own-cpu) so a
  // specialization can be linked to directly.
  const specializations = useMemo(() => {
    const counts = new Map<string, number>();
    courses.forEach((c) => {
      if (c.specialization) {
        counts.set(c.specialization, (counts.get(c.specialization) ?? 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, slug: slugify(name), count }));
  }, [courses]);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeSpec = specializations.find(
    (s) => s.slug === searchParams.get("specialization")
  );
  const selectSpec = (slug?: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set("specialization", slug);
    else next.delete("specialization");
    setSearchParams(next, { replace: true });
  };

  const visible = useMemo(() => {
    const filter = filters.find((f) => f.key === activeFilter) ?? filters[0];
    const q = query.trim().toLowerCase();
    return courses.filter(
      (c) =>
        filter.match(c) &&
        (!activeSpec || c.specialization === activeSpec.name) &&
        (!q ||
          [c.title, c.description, c.specialization]
            .filter(Boolean)
            .some((text) => String(text).toLowerCase().includes(q)))
    );
  }, [courses, activeFilter, query, activeSpec]);

  return (
    <div className="ss-catalog fade-in">
      <NavBar active="catalog" />
      <PageHeader
        crumb="Program Catalog"
        eyebrow="Program Catalog"
        title="Find a Program That Matches Your Interest"
        lead="Short courses, intensive workshops and self-paced on-demand courses in chip design, embedded systems, AI and more."
      >
        <Link to="/pathways" className="ss-btn ss-btn--ghost-light ss-btn--sm mt-4">
          <i className="bi bi-signpost-split" aria-hidden="true"></i>
          See the learning pathways
        </Link>
      </PageHeader>

      <section className="ss-catalog__toolbar-wrap">
        <div className="ss-container">
          <div className="ss-catalog__toolbar">
            <div className="ss-catalog__filters" role="group" aria-label="Program type">
              {filters.map((filter) => {
                const count = courses.filter(filter.match).length;
                return (
                  <button
                    key={filter.key}
                    type="button"
                    className={`ss-catalog__filter ${
                      activeFilter === filter.key ? "is-active" : ""
                    }`}
                    aria-pressed={activeFilter === filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                  >
                    {filter.label}
                    <span>{count}</span>
                  </button>
                );
              })}
            </div>
            <label className="ss-catalog__search">
              <i className="bi bi-search" aria-hidden="true"></i>
              <span className="visually-hidden">Search programs</span>
              <input
                type="search"
                placeholder="Search programs"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>
        </div>
      </section>

      <section className="ss-section ss-catalog__results">
        <div className="ss-container">
          <div className="ss-catalog__spec-head">
            <h2>Browse by specialization</h2>
            {activeSpec && (
              <button
                type="button"
                className="ss-catalog__spec-clear"
                onClick={() => selectSpec()}
              >
                <i className="bi bi-x-lg" aria-hidden="true"></i>
                Show all specializations
              </button>
            )}
          </div>
          <div className="ss-catalog__spec-grid" role="group" aria-label="Specialization">
            {specializations.map((spec) => {
              const active = activeSpec?.slug === spec.slug;
              return (
                <button
                  key={spec.slug}
                  type="button"
                  className={`ss-catalog__spec-tile ${active ? "is-active" : ""}`}
                  aria-pressed={active}
                  onClick={() => selectSpec(active ? undefined : spec.slug)}
                >
                  <span className="ss-catalog__spec-icon">
                    <i
                      className={`bi ${specIcons[spec.name] ?? "bi-bookmark-star"}`}
                      aria-hidden="true"
                    ></i>
                  </span>
                  <span className="ss-catalog__spec-name">{spec.name}</span>
                  <span className="ss-catalog__spec-count">
                    {spec.count} {spec.count === 1 ? "program" : "programs"}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="ss-catalog__count" aria-live="polite">
            Showing {visible.length} of {courses.length} programs
            {activeSpec && (
              <>
                {" "}in <strong>{activeSpec.name}</strong>
              </>
            )}
          </p>
          {visible.length > 0 ? (
            <div className="ss-grid ss-grid--3">
              {visible.map((course) => (
                <ProgramCard key={course.title} course={course} variant="detailed" />
              ))}
            </div>
          ) : (
            <div className="ss-catalog__empty">
              <i className="bi bi-search" aria-hidden="true"></i>
              <h2>No programs match your search</h2>
              <p>Try a different keyword or program type.</p>
              <button
                type="button"
                className="ss-btn ss-btn--outline ss-btn--sm"
                onClick={() => {
                  setQuery("");
                  setActiveFilter("all");
                  selectSpec();
                }}
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="ss-catalog__cta">
        <div className="ss-container">
          <div className="ss-catalog__cta-card">
            <div>
              <h2>Can't find what you're looking for?</h2>
              <p>
                Tell us what you'd like to learn, or request a tutor for
                content tailored to your institute.
              </p>
            </div>
            <div className="ss-catalog__cta-actions">
              <Link to="/request-a-tutor" className="ss-btn ss-btn--accent">
                Request a Tutor
              </Link>
              <Link to="/contact-us" className="ss-btn ss-btn--ghost-light">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer isFinancialInquiries={true} />
    </div>
  );
};

export default ProgramCatalog;
